import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  FileText,
  Save,
  Loader2,
  Activity,
  Hash
} from 'lucide-react';
import {
  PipelineDetail,
  StatusOption,
  createPipeline,
  updatePipeline,
  getStatusOptions,
  getPipelineById
} from '@/actions/data-fetch-pipelines';
import { getRoles } from '@/actions/data-fetch-roles';
import { RoleDetail } from '@/types/settings/RoleTypes';
import { toastMessage } from '@/utils/toast-message';

const PipelineFormSchema = z.object({
  pipeline_name: z.string().min(1, 'Pipeline name is required').max(100, 'Name must be less than 100 characters'),
  pipeline_description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  status_options: z.array(z.string()).min(1, 'At least one status is required'),
  role_uuid: z.string().optional()
});

type PipelineFormData = z.infer<typeof PipelineFormSchema>;

interface EnhancedPipelineFormProps {
  onClose?: () => void;
  uuid?: string;
  initialData?: PipelineDetail | null;
  onSuccess: (pipeline: PipelineDetail) => void;
}

const EnhancedPipelineForm: React.FC<EnhancedPipelineFormProps> = ({
  onClose,
  uuid,
  initialData,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<StatusOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'general' | 'statuses'>('general');
  const [availableRoles, setAvailableRoles] = useState<RoleDetail[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<PipelineFormData>({
    resolver: zodResolver(PipelineFormSchema),
    defaultValues: {
      pipeline_name: '',
      pipeline_description: '',
      status_options: [],
      role_uuid: ''
    }
  });

  // Load status options
  useEffect(() => {
    const loadStatusOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const response = await getStatusOptions();
        if (response.data?.options) {
          setAvailableStatuses(response.data.options);
        }
      } catch (error) {
        console.error('Error loading status options:', error);
        toastMessage('Failed to load status options', 'error');
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadStatusOptions();
  }, []);

  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setIsLoadingRoles(true);
        const response = await getRoles({ page: 1 });
        if (response.data?.roles) {
          setAvailableRoles(response.data.roles);
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        toastMessage('Failed to load roles', 'error');
      } finally {
        setIsLoadingRoles(false);
      }
    };

    loadRoles();
  }, []);

  // Load pipeline data if editing - wait for roles to load first
  useEffect(() => {
    // Don't load pipeline data until roles are loaded
    if (isLoadingRoles) return;

    const loadPipelineData = async () => {
      if (uuid && !initialData) {
        try {
          const response = await getPipelineById(uuid);
          if (response.data?.data) {
            const pipeline = response.data.data;

            // Set selected statuses first
            const statusUuids = pipeline.status_options?.map(opt => opt.uuid) || [];
            setSelectedStatuses(statusUuids);

            // Reset entire form with all values at once to ensure proper sync
            reset({
              pipeline_name: pipeline.pipeline_name || '',
              pipeline_description: pipeline.pipeline_description || '',
              status_options: statusUuids,
              role_uuid: pipeline.role_uuid || ''
            });
          }
        } catch (error) {
          console.error('Error loading pipeline data:', error);
          toastMessage('Failed to load pipeline data', 'error');
        }
      } else if (initialData) {
        const statusUuids = initialData.status_options?.map(opt => opt.uuid) || [];
        setSelectedStatuses(statusUuids);

        // Debug: Log what we're setting
        console.log('Setting form with initialData:', {
          role_uuid: initialData.role_uuid,
          assignedRole: initialData.assignedRole,
          availableRoles: availableRoles.length
        });

        // Reset entire form with all values at once
        reset({
          pipeline_name: initialData.pipeline_name || '',
          pipeline_description: initialData.pipeline_description || '',
          status_options: statusUuids,
          role_uuid: initialData.role_uuid || ''
        });
      }
    };

    loadPipelineData();
  }, [uuid, initialData, reset, isLoadingRoles, availableRoles]);

  const handleStatusToggle = (statusUuid: string) => {
    const updated = selectedStatuses.includes(statusUuid)
      ? selectedStatuses.filter(id => id !== statusUuid)
      : [...selectedStatuses, statusUuid];

    setSelectedStatuses(updated);
    setValue('status_options', updated);
  };

  const onSubmit = async (data: PipelineFormData) => {
    try {
      setIsSubmitting(true);

      // Ensure pipeline_description is always a string
      const submissionData = {
        ...data,
        pipeline_description: data.pipeline_description || ''
      };

      let response;
      if (uuid) {
        response = await updatePipeline(uuid, submissionData);
        toastMessage('Pipeline updated successfully', 'success');
      } else {
        response = await createPipeline(submissionData);
        toastMessage('Pipeline created successfully', 'success');
      }

      if (response.data?.data) {
        onSuccess(response.data.data);
      }

      onClose?.();
    } catch (error: any) {
      console.error('Error submitting pipeline:', error);
      toastMessage(
        error.message || `Failed to ${uuid ? 'update' : 'create'} pipeline`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'general'
                ? 'border-brandColor text-brandColor dark:text-brandColor'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              General Information
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('statuses')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'statuses'
                ? 'border-brandColor text-brandColor dark:text-brandColor'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Activity className="w-4 h-4 mr-2" />
              Status Configuration
              {selectedStatuses.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-brandColor/10 dark:bg-brandColor/20 text-brandColor dark:text-brandColor rounded-full">
                  {selectedStatuses.length}
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pipeline Name *
                  </label>
                  <input
                    {...register('pipeline_name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                    placeholder="e.g., Sales Pipeline, Lead Management"
                  />
                  {errors.pipeline_name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.pipeline_name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('pipeline_description')}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors resize-none"
                    placeholder="Describe the purpose and workflow of this pipeline..."
                  />
                  {errors.pipeline_description && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.pipeline_description.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Assign to Role (Optional)
                  </label>
                  <select
                    {...register('role_uuid')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors"
                    disabled={isLoadingRoles}
                  >
                    <option value="">All Roles (Default)</option>
                    {availableRoles.map((role) => (
                      <option key={role.uuid} value={role.uuid}>
                        {role.role_name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Leave unassigned to make this pipeline accessible to all roles, or select a specific role to restrict access.
                  </p>
                  {errors.role_uuid && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.role_uuid.message}
                    </p>
                  )}
                </div>

                {/* Pipeline Stats/Info */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Pipeline Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {uuid ? 'Existing Pipeline' : 'New Pipeline'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Selected Statuses:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {selectedStatuses.length} status{selectedStatuses.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statuses Tab */}
          {activeTab === 'statuses' && (
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  Select Status Options for This Pipeline
                </h3>

                {isLoadingOptions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableStatuses.length > 0 ? (
                      availableStatuses.map((status) => (
                        <label
                          key={status.uuid}
                          className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStatuses.includes(status.uuid)}
                            onChange={() => handleStatusToggle(status.uuid)}
                            className="h-4 w-4 text-brandColor rounded border-gray-300 focus:ring-brandColor"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center">
                              <Hash className="w-3 h-3 text-gray-400 mr-1" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {status.opt_name}
                              </span>
                              {status.opt_value && status.opt_value !== status.opt_name && (
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                  ({status.opt_value})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>Order: {status.opt_order}</span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              status.opt_active
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                            }`}>
                              {status.opt_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No status options available. Please create status options first.
                      </div>
                    )}
                  </div>
                )}

                {errors.status_options && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.status_options.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Form Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex justify-end space-x-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting || (activeTab === 'statuses' && selectedStatuses.length === 0)}
              className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover disabled:bg-brandColor/40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brandColor focus:ring-offset-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uuid ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {uuid ? 'Update Pipeline' : 'Create Pipeline'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EnhancedPipelineForm;